import { DatesRangeValue } from '@mantine/dates';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useLayoutEffect, useState } from 'react';

import { useBot } from '@mygpt/app/bot';
import { DictionaryItem, getDictionaryService } from '@mygpt/app/dictionary/services';

// 获取一个月前的日期
const oneMonthAgo = new Date();
oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
oneMonthAgo.setHours(0, 0, 0, 0);

const today = new Date();
today.setHours(23, 59, 59, 999);

dayjs.extend(utc);

export const useDictionaryList = () => {
  const { botId } = useBot();
  const [loading, setLoading] = React.useState(false);

  const [data, setData] = useState<DictionaryItem[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [search, setSearch] = useState('');
  const [dateValue, setDateValue] = useState<DatesRangeValue>([oneMonthAgo, today]);
  const [page, setPage] = React.useState<{ page: number; total: number; size: number; pages: number }>({
    page: 1,
    total: data.length,
    pages: 1,
    size: 50,
  });

  const getQueryParams = (customParam?: Omit<Parameters<typeof getDictionaryService>[0], 'aiId'>) => {
    return Object.assign(
      { page: page.page, size: page.size, search },
      dateValue[0] &&
        dateValue[1] && {
          start_date: dayjs(dateValue[0]).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
          end_date: dayjs(dateValue[1]).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
        },
      customParam
    );
  };

  const loadList = React.useCallback(async (params: Omit<Parameters<typeof getDictionaryService>[0], 'aiId'>) => {
    setLoading(true);
    return getDictionaryService({ aiId: botId, order_by: '-updated_at', size: page.size, ...params })
      .then((res) => {
        setData(res.data.items);
        setPage({
          page: res.data.page,
          total: res.data.total,
          pages: res.data.pages,
          size: res.data.size,
        });
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handlePageChange = React.useCallback(
    (options: Partial<{ page: number; size: number }>) => setPage((prev) => ({ ...prev, ...options })),
    []
  );

  useLayoutEffect(() => {
    if ((dateValue[0] && dateValue[1]) || (!dateValue[0] && !dateValue[1])) {
      loadList(getQueryParams({ page: 1 }));
    }
  }, [dateValue, search]);

  useLayoutEffect(() => {
    loadList(getQueryParams());
  }, [page.page, page.size]);

  return {
    error,
    loading,
    data,
    page,
    dateValue,
    search,
    setPage: handlePageChange,
    setDateValue,
    setSearch,
    loadList,
    getQueryParams,
  };
};
